/**
 * Animation components — thin wrappers around the primitive hooks.
 *
 *  <CountUp end={32} suffix="B+" />
 *  <Magnetic strength={0.2}> <button>... </button> </Magnetic>
 *  <Reveal delay={120}> ...content... </Reveal>
 *
 * For staggered lists, prefer a parent `<RevealStack>` with `<RevealStack.Item>`
 * children — one IntersectionObserver per stack, CSS handles the cascade via
 * a custom property `--reveal-delay`.
 */
import { useCountUp, useMagnetic, useReveal } from "../../hooks/useAnimations";

/* ------------------------- CountUp ------------------------- */
export function CountUp({ end, prefix = "", suffix = "", duration = 1500, decimals = 0, className = "" }) {
  const [ref, value] = useCountUp({ end, duration, decimals });
  return (
    <span ref={ref} className={className} data-testid="count-up">
      {prefix}
      {value}
      {suffix}
    </span>
  );
}

/* ------------------------- Magnetic ------------------------ */
export function Magnetic({ children, strength = 0.25, className = "" }) {
  const ref = useMagnetic({ strength });
  return (
    <span ref={ref} className={`inline-block ${className}`}>
      {children}
    </span>
  );
}

/* ------------------------- Reveal -------------------------- */
export function Reveal({ children, delay = 0, className = "", as: Tag = "div", once = true, ...rest }) {
  const [ref, revealed] = useReveal({ once });
  return (
    <Tag
      ref={ref}
      className={`reveal ${revealed ? "is-revealed" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ------------------------- RevealStack --------------------- *
 * Stagger a list of children by `step` ms each.
 */
export function RevealStack({ children, step = 90, className = "", once = true, baseDelay = 0 }) {
  const [ref, revealed] = useReveal({ once });
  const items = Array.isArray(children) ? children : [children];
  return (
    <div ref={ref} className={`reveal-stack ${revealed ? "is-revealed" : ""} ${className}`}>
      {items.map((child, i) => (
        <div
          key={i}
          className="reveal-item"
          style={{ "--reveal-delay": `${baseDelay + i * step}ms` }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
